<script setup lang="ts">
import { ref, watchEffect, computed, toRefs } from 'vue';
import { storeToRefs } from 'pinia';
import { useBookmarks } from '@ilteducation/annotations';
import type { NewAnnotation } from '@ilteducation/annotations-types';
import useMediaStore from '../../stores/media';
import useReaderEngine from '../../engine/use-reader-engine';
import useLocale from '../../composables/useLocale';
import { createEpubCFI, getLocation } from '../../util/location';
import type { Track } from '../../types';
import useUiStore from '../../stores/ui';
import useNavigationStore from '../../stores/navigation';
import BookmarkFormDialog from './BookmarkFormDialog.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faBookmark as faBookmarkSolid } from '@fortawesome/pro-solid-svg-icons';
import { faBookmark } from '@fortawesome/pro-regular-svg-icons';

library.add(faBookmark, faBookmarkSolid);

type Emits = {
  (e: 'bookmarkCreated'): void;
};
const emit = defineEmits<Emits>();

type Props = {
  bookId: string;
  btnType?: 'button' | 'icon';
  showTooltips?: boolean;
};
const props = withDefaults(defineProps<Props>(), {
  btnType: 'icon',
  showTooltips: true,
});
const { bookId } = toRefs(props);

const { t } = useLocale();
const { create } = useBookmarks(bookId, undefined, { autoLoad: false });

const dialog = ref(false);
const isSaving = ref(false);

const isBtn = computed(() => props.btnType === 'button');

const { currentTrack, playing } = storeToRefs(useMediaStore());
const { getSpineByIdentifier } = useReaderEngine();
const { closeTableOfContentDrawer, closeAnnotationsDrawer } = useUiStore();
const navigationStore = useNavigationStore();
const { getPageByHref, getTocByHref } = navigationStore;
const { currentTocItem } = storeToRefs(navigationStore);

const getCurrentLabelFromTrack = (track?: Track) => {
  const href = track?.text.src;
  if (!href) return '';

  const navigationItem = getTocByHref(href);
  if (navigationItem) {
    return navigationItem.label;
  }

  const page = getPageByHref(href);
  if (page) {
    return t('book_player.label_bookmark_page_nr', { pageNr: page });
  }

  const { src, srcFragmentId } = track.text || {};
  const spine = getSpineByIdentifier(src);
  const text = spine?.document?.getElementById(srcFragmentId)?.innerText;
  if (text) {
    return text.length > 30 ? `${text.substring(0, 30)}...` : text;
  }

  return '';
};

const defaultBookmarkName = ref('');

watchEffect(() => {
  if (dialog.value) {
    if (playing.value) {
      playing.value = false;
    }
    defaultBookmarkName.value = getCurrentLabelFromTrack(currentTrack.value) || currentTocItem.value?.label || '';
    closeTableOfContentDrawer();
    closeAnnotationsDrawer();
  }
});

const createBookmark = async (name: string, comment: string) => {
  try {
    isSaving.value = true;
    const location = await getLocation();
    const epubCfi = createEpubCFI(location);
    const newBookmark: NewAnnotation = {
      contentId: bookId.value,
      name,
      type: 'bookmark',
      position: epubCfi,
      comment: comment ? { type: 'text', content: comment } : undefined,
    };
    await create(newBookmark);
    dialog.value = false;
    emit('bookmarkCreated');
  } catch (err) {
    console.error('Failed to create bookmark:', err);
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <v-btn
    id="bookmarkBtn"
    data-testid="bp-add-bookmark-btn"
    color="on-primary"
    :variant="isBtn ? 'flat' : 'text'"
    :ripple="false"
    :icon="!isBtn"
    :block="!!isBtn"
    :aria-label="t('book_player.button_add_new_bookmark')"
    :title="t('book_player.button_add_new_bookmark')"
    size="48"
    :key="`bookmark-btn-${dialog}`"
    @click="dialog = true"
  >
    <template #prepend>
      <FontAwesomeIcon v-if="!isBtn" :icon="dialog ? 'fas fa-bookmark' : 'far fa-bookmark'" />
    </template>
    <FontAwesomeIcon v-if="!isBtn" size="xl" :icon="dialog ? 'fas fa-bookmark' : 'far fa-bookmark'" />
    <span v-if="isBtn">{{ t('book_player.button_add_new_bookmark') }}</span>
    <v-tooltip
      :disabled="!showTooltips"
      activator="parent"
      location="top"
      :text="t('book_player.button_add_new_bookmark')"
    />
  </v-btn>

  <BookmarkFormDialog v-model="dialog" :is-saving="isSaving" :is-removing="false" @save="createBookmark" />
</template>

<style scoped lang="scss"></style>
